import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// ============================================================================
// TYPES
// ============================================================================

export interface RentalMetric {
  name: string;
  value: number;
  category: string;
  description?: string;
  unit?: string;
}

export interface RentalUtilizationData {
  id?: number;
  organizationId: number;
  facilityCode: string;
  facilityName: string;
  equipmentTypeCode?: string;
  equipmentTypeDescription?: string;
  year: number;
  month: number;
  reportDate?: string;
  dataSource: 'monthly_utilization' | 'summary';
  sourceFile?: string;
  metrics: RentalMetric[];
  createdAt?: string;
  updatedAt?: string;
  isActive: boolean;
}

export interface RentalEquipmentUnit {
  id?: number;
  organizationId: number;
  unitNumber: string;
  facilityCode: string;
  facilityName?: string;
  equipmentGroup?: string;
  equipmentTypeCode?: string;
  equipmentTypeDescription?: string;
  status?: string;
  regionCode?: string;
  pool?: string;
  unitYear?: number;
  make?: string;
  model?: string;
  unitDescription?: string;
  serialNumber?: string;
}

export interface ImportResult {
  success: boolean;
  message: string;
  fileType: 'monthly_utilization' | 'summary' | 'equipment_master' | 'unknown';
  data?: {
    created: number;
    updated: number;
    failed: number;
    errors: Array<{ row: number; error: string }>;
    warnings: string[];
  };
}

export interface FacilitySummary {
  facilityCode: string;
  facilityName: string;
}

export interface EquipmentTypeSummary {
  equipmentTypeCode: string;
  equipmentTypeDescription: string;
}

export interface EquipmentFacilitySummary {
  facilityCode: string;
  facilityName: string;
  totalUnits: number;
  activeUnits: number;
  equipmentTypes: number;
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class RentalUtilizationService {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
    };
  }

  /**
   * Import rental data from CSV file
   */
  async importFromCSV(file: File, organizationId?: number): Promise<ImportResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (organizationId) {
        formData.append('organization_id', organizationId.toString());
      }

      const response = await axios.post(
        `${API_BASE_URL}/rental-utilization/import`,
        formData,
        {
          headers: {
            ...this.getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      return response.data;
    } catch (error: any) {
      console.error('Error importing rental data:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to import rental data'
      );
    }
  }

  /**
   * Get all rental utilization data
   */
  async getAll(): Promise<RentalUtilizationData[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rental-utilization`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch data');
    } catch (error: any) {
      console.error('Error fetching rental utilization data:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch rental utilization data'
      );
    }
  }

  /**
   * Get rental utilization data by facility
   */
  async getByFacility(facilityCode: string): Promise<RentalUtilizationData[]> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/rental-utilization/by-facility`,
        { facilityCode },
        { headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch data');
    } catch (error: any) {
      console.error('Error fetching rental utilization by facility:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch data'
      );
    }
  }

  /**
   * Get rental utilization data by date range
   */
  async getByDateRange(
    startYear: number,
    startMonth: number,
    endYear: number,
    endMonth: number
  ): Promise<RentalUtilizationData[]> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/rental-utilization/by-date-range`,
        { startYear, startMonth, endYear, endMonth },
        { headers: { ...this.getAuthHeaders(), 'Content-Type': 'application/json' } }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch data');
    } catch (error: any) {
      console.error('Error fetching rental utilization by date range:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch data'
      );
    }
  }

  /**
   * Get unique facilities
   */
  async getFacilities(): Promise<FacilitySummary[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rental-utilization/facilities`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch facilities');
    } catch (error: any) {
      console.error('Error fetching facilities:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch facilities'
      );
    }
  }

  /**
   * Get unique equipment types
   */
  async getEquipmentTypes(): Promise<EquipmentTypeSummary[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rental-utilization/equipment-types`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch equipment types');
    } catch (error: any) {
      console.error('Error fetching equipment types:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch equipment types'
      );
    }
  }

  /**
   * Get all equipment units
   */
  async getEquipmentUnits(): Promise<RentalEquipmentUnit[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rental-utilization/equipment-units`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch equipment units');
    } catch (error: any) {
      console.error('Error fetching equipment units:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch equipment units'
      );
    }
  }

  /**
   * Get equipment summary by facility
   */
  async getEquipmentSummary(): Promise<EquipmentFacilitySummary[]> {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/rental-utilization/equipment-units/summary`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return response.data.data;
      }
      throw new Error(response.data.message || 'Failed to fetch equipment summary');
    } catch (error: any) {
      console.error('Error fetching equipment summary:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to fetch equipment summary'
      );
    }
  }

  /**
   * Delete all rental utilization data (soft delete)
   */
  async deleteAll(): Promise<{ message: string }> {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/rental-utilization`,
        { headers: this.getAuthHeaders() }
      );

      if (response.data.success) {
        return { message: response.data.message };
      }
      throw new Error(response.data.message || 'Failed to delete data');
    } catch (error: any) {
      console.error('Error deleting rental utilization data:', error);
      throw new Error(
        error.response?.data?.message || 
        error.message || 
        'Failed to delete data'
      );
    }
  }
}

export default new RentalUtilizationService();

